import { ModalDialog } from "@/components/data-display/modal-dialog";
import { Input } from "@/components/inputs/input";
import { Button } from "@/components/ui/button";
import { createRef, useEffect } from "react";
import { CopyButton } from "../../../features/private-room/list/layouts/private-room-admin-details/shared/copy-button";
import {
  LoaderFunctionArgs,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const proposalLongUrl = url.searchParams.get("proposalLongUrl") ?? "";
  const proposalShortUrl = url.searchParams.get("proposalShortUrl") ?? "";
  const ids = url.searchParams
    .getAll("spaceIds[]")
    .map((id: string) => Number(id));

  const excludeParams = [
    "proposalLongUrl",
    "proposalShortUrl",
    "spaceIds[]",
    "deal_id",
    "credits_price",
    "ec",
  ];

  const filters = Array.from(url.searchParams.entries())
    .filter(([key]) => !excludeParams.includes(key))
    .reduce(
      (acc, [key, value]) => {
        if (acc[key]) {
          if (Array.isArray(acc[key])) {
            (acc[key] as string[]).push(value);
          } else {
            acc[key] = [acc[key] as string, value];
          }
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string | string[]>,
    );

  return { proposalLongUrl, proposalShortUrl, ids, filters };
};

type ProposalLinksLoader = Awaited<ReturnType<typeof loader>>;

export function ModalProposalLink() {
  const { proposalLongUrl, proposalShortUrl, ids, filters } =
    useLoaderData() as ProposalLinksLoader;
  const { pathname } = useLocation();
  const longUrlRef = createRef<HTMLInputElement>();
  const shortUrlRef = createRef<HTMLInputElement>();
  const navigate = useNavigate();

  useEffect(() => {
    sendTrackDataServer({
      url: pathname,
      eventName: "CLICK_PRIVATEOFFICE_PROPOSAL_FINAL",
      productType: "PO",
      data: {
        private_office_ids: ids,
        proposalLongUrl,
        dealUrl: proposalLongUrl.split("deal_id=")[1],
        filters: filters,
      },
    });
  }, []);

  return (
    <ModalDialog
      type="submit"
      title="Sucesso!"
      description={`Sua proposta foi criada com sucesso! Copie os links abaixo para compartilhar.`}
      open
      handleClose={() => {
        navigate(-1);
      }}
      handleConfirm={() => {}}
      maxMidth="p-0 max-w-[404px]"
      showFooter={false}
      enableButton={true}
      titleStyle="text-xl pt-0 px-6 pb-0 zinc-950 text-center"
      descriptionStyle="text-sm px-6 text-zinc-500 mt-0 text-center"
      headerStyle="text-center items-center justify-center"
      image="/images/ConfirmedIcon.svg"
      imageStyle="pt-6"
    >
      <div className="pt-0 pb-6">
        <div className="px-6 pb-6">
          <div className="flex flex-col gap-3">
            <div className="flex gap-3 justify-end items-end">
              <div className="flex-1">
                <Input
                  ref={longUrlRef}
                  className="w-full flex-initial"
                  label="URL Longa"
                  name="long_url"
                  type="string"
                  showlabel={true}
                  defaultValue={proposalLongUrl}
                  readOnly
                />
              </div>
              <CopyButton
                size="w-[40px] h-[40px]"
                action={() => {
                  if (longUrlRef.current) {
                    longUrlRef.current.select();
                    document.execCommand("copy");
                  }
                }}
              />
            </div>

            <div className="flex gap-3 justify-end items-end">
              <div className="flex-1">
                <Input
                  ref={shortUrlRef}
                  className="w-full flex-initial"
                  label="URL Encurtada"
                  name="short_url"
                  type="string"
                  showlabel={true}
                  defaultValue={proposalShortUrl}
                  readOnly
                />
              </div>
              <CopyButton
                size="w-[40px] h-[40px]"
                action={() => {
                  if (shortUrlRef.current) {
                    shortUrlRef.current.select();
                    document.execCommand("copy");
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="border-t border-zinc-200 px-6 pt-6">
          <div className="flex flex-col">
            <Button
              type="button"
              className="min-h-[40px]"
              onClick={() => navigate(-1)}
            >
              Entendido
            </Button>
          </div>
        </div>
      </div>
    </ModalDialog>
  );
}
